import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
	Alert,
	Card,
	Col,
	Container,
	Row,
	Spinner,
	Table,
} from "react-bootstrap";
import {
	Activity,
	ArrowUpRight,
	ChartBar,
	ClipboardList,
	Heart,
	Star,
	UserCheck,
	Users,
} from "lucide-react";
import api from "../api/api";
import "../css/MetricsDashboard.css";

const numberFormat = new Intl.NumberFormat("en-NZ");

const formatNumber = (value) => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return "0";
	}

	return numberFormat.format(value);
};

const formatDecimal = (value, decimals = 1) => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return "0";
	}

	return Number(value).toFixed(decimals);
};

const formatPercent = (value, decimals = 0) => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return "0%";
	}

	return `${Number(value).toFixed(decimals)}%`;
};

const getActiveTotalText = (active, total) => {
	return `${formatNumber(active)} / ${formatNumber(total)}`;
};

const getPercentage = (active, total) => {
	if (!total || total <= 0) {
		return 0;
	}

	return Math.min(100, Math.round((active / total) * 100));
};

const getRate = (active, total) => {
	if (!total || total <= 0) {
		return 0;
	}

	return Math.min(100, (active / total) * 100);
};

const getTherapistName = (therapist) => {
	const firstName = therapist.user__first_name || therapist.first_name || "";
	const lastName = therapist.user__last_name || therapist.last_name || "";
	const email = therapist.user__email || therapist.email || "";
	const fullName = `${firstName} ${lastName}`.trim();

	return fullName || email || "Unknown therapist";
};

const getTherapistEmail = (therapist) => {
	return therapist.user__email || therapist.email || "No email";
};

const getClinicStatus = (clinic) => {
	if (clinic.status) {
		return clinic.status;
	}

	if ((clinic.active_therapists || 0) > 0 || (clinic.active_clients || 0) > 0) {
		return "Active";
	}

	return "Inactive";
};

const StatCard = ({ icon: Icon, value, label, variant, subText }) => {
	return (
		<Card className="metrics-stat-card">
			<Card.Body>
				<div className={`metrics-stat-icon metrics-stat-icon-${variant}`}>
					<Icon size={24} />
				</div>

				<div className="metrics-stat-content">
					<div className="metrics-stat-value">{value}</div>
					<div className="metrics-stat-label">{label}</div>

					{subText && (
						<div className="metrics-stat-subtext">
							<ArrowUpRight size={13} />
							<span>{subText}</span>
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

StatCard.propTypes = {
	icon: PropTypes.elementType.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	label: PropTypes.string.isRequired,
	variant: PropTypes.oneOf([
		"pink",
		"green",
		"blue",
		"red",
		"purple",
		"orange",
	]).isRequired,
	subText: PropTypes.string,
};

const CompactProgress = ({ active, total }) => {
	const percentage = getPercentage(active, total);

	return (
		<div className="metrics-progress-cell">
			<span>{getActiveTotalText(active, total)}</span>
			<div className="metrics-progress-track">
				<div
					className="metrics-progress-fill"
					style={{ width: `${percentage}%` }}
				/>
			</div>
		</div>
	);
};

CompactProgress.propTypes = {
	active: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
};

const EngagementItem = ({ icon: Icon, variant, label, value, suffix, helperText }) => {
	return (
		<div className="engagement-item">
			<div className={`engagement-icon engagement-icon-${variant}`}>
				<Icon size={20} />
			</div>

			<div>
				<div className="engagement-label">{label}</div>
				<div className="engagement-value">
					{value}
					{suffix}
				</div>

				{helperText && (
					<div className="engagement-helper">
						{helperText}
					</div>
				)}
			</div>
		</div>
	);
};

EngagementItem.propTypes = {
	icon: PropTypes.elementType.isRequired,
	variant: PropTypes.oneOf(["purple", "orange", "blue", "green"]).isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	suffix: PropTypes.string,
	helperText: PropTypes.string,
};

const MetricsDashboard = () => {
	const [metrics, setMetrics] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showAllClinics, setShowAllClinics] = useState(false);
	const [showAllTherapists, setShowAllTherapists] = useState(false);

	const fetchMetrics = async () => {
		try {
			setLoading(true);
			setError(null);

			const response = await api.get("/metrics/");

			setMetrics(response.data);
		} catch (err) {
			console.error("Error fetching metrics:", err);
			setError("Failed to load metrics. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchMetrics();
	}, []);

	const sortedClinics = useMemo(() => {
		return [...(metrics?.clinics || [])].sort((a, b) => {
			const aStatus = getClinicStatus(a).toLowerCase();
			const bStatus = getClinicStatus(b).toLowerCase();
			const aIsActive = aStatus === "active" ? 1 : 0;
			const bIsActive = bStatus === "active" ? 1 : 0;

			if (aIsActive !== bIsActive) {
				return bIsActive - aIsActive;
			}

			return (b.active_therapists || 0) - (a.active_therapists || 0);
		});
	}, [metrics]);

	const sortedTherapists = useMemo(() => {
		return [...(metrics?.therapist_performance || metrics?.programs?.by_therapist || [])].sort((a, b) => {
			const bClientsAdded = b.clients_added_last_7_days || 0;
			const aClientsAdded = a.clients_added_last_7_days || 0;

			if (bClientsAdded !== aClientsAdded) {
				return bClientsAdded - aClientsAdded;
			}

			return (b.programs_created || b.program_count || 0) - (a.programs_created || a.program_count || 0);
		});
	}, [metrics]);

	const visibleClinics = showAllClinics
		? sortedClinics
		: sortedClinics.slice(0, 10);

	const visibleTherapists = showAllTherapists
		? sortedTherapists
		: sortedTherapists.slice(0, 10);

	const totalProgramsCreated = useMemo(() => {
		return sortedTherapists.reduce(
			(total, therapist) => total + (therapist.programs_created || therapist.program_count || 0),
			0
		);
	}, [sortedTherapists]);

	const activeTherapistsLast30Days = metrics?.engagement?.active_therapists_last_30_days || 0;
	const totalTherapists = metrics?.users?.therapists || 0;
	const activeTherapistRate = metrics?.engagement?.active_therapist_rate ?? getRate(activeTherapistsLast30Days, totalTherapists);

	const clientSevenDayFollowThroughRate =
		metrics?.engagement?.client_7_day_follow_through_rate ??
		metrics?.engagement?.client_follow_through_rate ??
		0;

	if (loading && !metrics) {
		return (
			<Container fluid className="metrics-page">
				<div className="metrics-loading-state">
					<Spinner animation="border" size="sm" />
					<span>Loading metrics...</span>
				</div>
			</Container>
		);
	}

	if (error && !metrics) {
		return (
			<Container fluid className="metrics-page">
				<Alert variant="danger" className="mb-0">
					{error}
				</Alert>
			</Container>
		);
	}

	if (!metrics) {
		return (
			<Container fluid className="metrics-page">
				<div className="metrics-empty-state">
					<ChartBar size={28} />
					<span>No metrics available</span>
				</div>
			</Container>
		);
	}

	return (
		<Container fluid className="metrics-page">
			<div className="metrics-shell">
				<div className="metrics-header">
					<div>
						<div className="metrics-title-row">
							<div className="metrics-title-icon">
								<ChartBar size={28} />
							</div>

							<div>
								<h1>Metrics</h1>
								<p>
									Overview of clinic adoption, therapist activity and client follow-through.
								</p>
							</div>
						</div>
					</div>
				</div>

				{error && (
					<Alert variant="warning" className="mb-3">
						{error}
					</Alert>
				)}

				<Row className="g-2 metrics-stat-grid row-cols-1 row-cols-sm-2 row-cols-xl-5">
					<Col>
						<StatCard
							icon={UserCheck}
							value={formatNumber(metrics.users.active)}
							label="Active Users"
							variant="green"
							subText={`${formatNumber(metrics.users.last_30_days?.active || 0)} new in last 30 days`}
						/>
					</Col>

					<Col>
						<StatCard
							icon={Users}
							value={formatNumber(metrics.users.therapists)}
							label="Therapists"
							variant="blue"
							subText={`${formatNumber(metrics.users.last_30_days?.therapists || 0)} new in last 30 days`}
						/>
					</Col>

					<Col>
						<StatCard
							icon={Heart}
							value={formatNumber(metrics.users.clients)}
							label="Clients"
							variant="red"
							subText={`${formatNumber(metrics.users.last_30_days?.clients || 0)} new in last 30 days`}
						/>
					</Col>

					<Col>
						<StatCard
							icon={ClipboardList}
							value={formatNumber(metrics.programs?.total || totalProgramsCreated)}
							label="Programs Created"
							variant="purple"
							subText={`${formatNumber(metrics.programs?.last_30_days || 0)} created in last 30 days`}
						/>
					</Col>

					<Col>
						<StatCard
							icon={Activity}
							value={formatNumber(metrics.exercise_feedback.total)}
							label="Feedback Submitted"
							variant="orange"
							subText={`${formatNumber(metrics.exercise_feedback.last_30_days || 0)} submitted in last 30 days`}
						/>
					</Col>
				</Row>

				<Row className="g-3 mt-1">
					<Col lg={12}>
						<Card className="metrics-panel h-100">
							<Card.Body>
								<div className="metrics-panel-header">
									<div>
										<h2>Engagement Overview</h2>
										<p>Percentage-based view of therapist and client activity.</p>
									</div>
								</div>

								<div className="engagement-list engagement-list-grid">
									<EngagementItem
										icon={UserCheck}
										variant="green"
										label="Active Therapists"
										value={formatPercent(activeTherapistRate)}
										helperText={`${formatNumber(activeTherapistsLast30Days)} of ${formatNumber(totalTherapists)} therapists prescribed at least 1 program in the last 30 days`}
									/>

									<EngagementItem
										icon={Heart}
										variant="orange"
										label="Client 7 Day Follow-through"
										value={formatPercent(clientSevenDayFollowThroughRate)}
										helperText="Clients who completed at least 1 assigned action in the last 7 days"
									/>

									<EngagementItem
										icon={Star}
										variant="purple"
										label="Avg Completions Per Active Client"
										value={formatDecimal(metrics.engagement?.average_completions_per_active_client || 0)}
										helperText="Average completed actions from clients considered active"
									/>
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				<Row className="g-3 mt-1">
					<Col xl={12}>
						<Card className="metrics-panel">
							<Card.Body>
								<div className="metrics-panel-header">
									<div>
										<h2>Clinic Performance</h2>
										<p>Clinics ordered by current therapist activity.</p>
									</div>
								</div>

								<Table responsive hover className="metrics-table align-middle mb-0">
									<thead>
										<tr>
											<th>Clinic Name</th>
											<th className="text-end">Therapists</th>
											<th className="text-end">Clients</th>
										</tr>
									</thead>

									<tbody>
										{visibleClinics.map((clinic) => (
											<tr key={clinic.name}>
												<td className="clinic-name-cell">{clinic.name}</td>
												<td className="text-end">
													<CompactProgress
														active={clinic.active_therapists || 0}
														total={clinic.total_therapists || 0}
													/>
												</td>
												<td className="text-end">
													<CompactProgress
														active={clinic.active_clients || 0}
														total={clinic.total_clients || 0}
													/>
												</td>
											</tr>
										))}
									</tbody>
								</Table>

								<div className="metrics-panel-footer metrics-panel-footer-action">
									<span>
										Showing {visibleClinics.length} of {formatNumber(sortedClinics.length)} clinics
									</span>

									{sortedClinics.length > 10 && (
										<button
											type="button"
											className="metrics-link-button"
											onClick={() => setShowAllClinics((current) => !current)}
										>
											{showAllClinics ? "Show fewer clinics" : "View all clinics"}
										</button>
									)}
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				<Row className="g-3 mt-1">
					<Col xl={12}>
						<Card className="metrics-panel">
							<Card.Body>
								<div className="metrics-panel-header">
									<div>
										<h2>Therapist Performance</h2>
										<p>How quickly therapists are adding clients and continuing to create programs.</p>
									</div>
								</div>

								<Table responsive hover className="metrics-table align-middle mb-0">
									<thead>
										<tr>
											<th>Therapist</th>
											<th>Email</th>
											<th className="text-end">Clients Added In 7 Days</th>
											<th className="text-end">Total Clients</th>
											<th className="text-end">Programs Created</th>
										</tr>
									</thead>

									<tbody>
										{visibleTherapists.length > 0 ? (
											visibleTherapists.map((therapist, index) => (
												<tr key={getTherapistEmail(therapist) || index}>
													<td className="clinic-name-cell">{getTherapistName(therapist)}</td>
													<td>{getTherapistEmail(therapist)}</td>
													<td className="text-end">
														{formatNumber(therapist.clients_added_last_7_days || 0)}
													</td>
													<td className="text-end">
														{formatNumber(therapist.total_clients || therapist.client_count || 0)}
													</td>
													<td className="text-end">
														{formatNumber(therapist.programs_created || therapist.program_count || 0)}
													</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan={5}>
													<div className="metrics-muted-empty">
														No therapist performance data available.
													</div>
												</td>
											</tr>
										)}
									</tbody>
								</Table>

								<div className="metrics-panel-footer metrics-panel-footer-action">
									<span>
										Showing {visibleTherapists.length} of {formatNumber(sortedTherapists.length)} therapists
									</span>

									{sortedTherapists.length > 10 && (
										<button
											type="button"
											className="metrics-link-button"
											onClick={() => setShowAllTherapists((current) => !current)}
										>
											{showAllTherapists ? "Show fewer therapists" : "View all therapists"}
										</button>
									)}
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</div>
		</Container>
	);
};

export default MetricsDashboard;